import React, { type ReactNode } from "react";
import { Hash, type LucideIcon } from "lucide-react";
import { SALAO_CARD_SHELL_CLASS } from "../utils/origemPainel";
import {
  PAINEL_URGENCIA_SHELL_CLASS,
  type PainelCardUrgencia,
} from "../utils/painelCardUrgencia";

export type PainelCardVariant = "default" | "prioritario" | "finalizado";

const shellClass: Record<Exclude<PainelCardVariant, "default">, string> = {
  prioritario:
    "overflow-hidden rounded-xl border border-red-300 bg-red-50 shadow-sm",
  finalizado:
    "overflow-hidden rounded-xl border border-emerald-200/80 bg-emerald-50/40 shadow-sm",
};

/** Fundo dark red só para pedidos salão FoodFly (demais fluxos usam urgência/padrão). */
const shellSalaoFoodflyClass = SALAO_CARD_SHELL_CLASS;


const displayChipClass: Record<"default" | "finalizado", string> = {
  default:
    "inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-slate-700",
  finalizado:
    "inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-slate-700",
};

export interface PainelCardShellProps {
  variant?: PainelCardVariant;
  urgencia?: PainelCardUrgencia;
  /** Pedido salão FoodFly: fundo vermelho. Entrega/cardápio/iFood não usam isto. */
  isSalao?: boolean;
  displayChip?: "default" | "finalizado";
  displayId: number;
  /** Sobrescreve o chip (ex.: "Mesa 5" / "Comanda #2" no salão). */
  badgeLabel?: ReactNode;
  /** Substitui o chip padrão por badges completos (Pedido · Mesa · Comanda). */
  headerBadges?: ReactNode;
  headerEnd?: ReactNode;
  headerClassName?: string;
  /** Menos padding e chip menor. Não altera os cards das colunas ao vivo. */
  dense?: boolean;
  children: ReactNode;
  className?: string;
}

function resolveShellClass(
  variant: PainelCardVariant,
  urgencia: PainelCardUrgencia,
  isSalao: boolean,
): string {
  if (variant === "prioritario") {
    return shellClass.prioritario;
  }
  if (isSalao) {
    return shellSalaoFoodflyClass;
  }
  if (variant === "finalizado") {
    return shellClass.finalizado;
  }
  return PAINEL_URGENCIA_SHELL_CLASS[urgencia];
}

export const PainelCardShell: React.FC<PainelCardShellProps> = ({
  variant = "default",
  urgencia = "normal",
  isSalao = false,
  displayChip = "default",
  displayId,
  badgeLabel,
  headerBadges,
  headerEnd,
  headerClassName = "mb-4 flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1.5",
  dense = false,
  children,
  className = "",
}) => {
  const chipClass = dense
    ? "inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-slate-700"
    : displayChipClass[displayChip];

  return (
    <div
      data-salao-card={isSalao ? "" : undefined}
      className={`${resolveShellClass(variant, urgencia, isSalao)} ${className}`.trim()}
    >
      <div className={dense ? "px-2.5 py-2" : "p-4"}>
        <div className={headerClassName}>
          {headerBadges != null ? (
            <div className="min-w-0 flex-1 basis-full sm:basis-auto">
              {headerBadges}
            </div>
          ) : (
            <span className={`${chipClass} min-w-0 max-w-full shrink`}>
              <Hash size={dense ? 12 : 16} className="shrink-0" />
              <span className="truncate">{badgeLabel ?? displayId}</span>
            </span>
          )}
          {headerEnd != null && (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {headerEnd}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export const PainelCardDetailRow: React.FC<{
  icon: LucideIcon;
  children: ReactNode;
}> = ({ icon: Icon, children }) => (
  <div className="flex min-h-[1.5rem] items-center gap-2 py-2 text-sm text-slate-600 [[data-salao-card]_&]:text-white/90">
    <Icon
      size={16}
      className="shrink-0 text-slate-500 [[data-salao-card]_&]:text-white/75"
    />
    <span>{children}</span>
  </div>
);

export const PainelCardAmberNote: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm leading-relaxed text-amber-900">
    {children}
  </div>
);

export const PainelCardAmberObsPizza: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="mt-3 rounded-lg border border-amber-300/90 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-950">
    {children}
  </div>
);

type TemposTone = "bordered" | "compact";

export const PainelCardTemposSection: React.FC<{
  children: ReactNode;
  tone?: TemposTone;
}> = ({ children, tone = "bordered" }) => {
  if (tone === "compact") {
    return <div className="mt-3">{children}</div>;
  }
  return (
    <div className="mt-4 border-t border-slate-100 pt-4 [[data-salao-card]_&]:border-white/25">
      {children}
    </div>
  );
};
